import React, { useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Tooltip } from "../ui/tooltip";
import { Info } from "lucide-react";
import AgentActionAPIEdit from "./AgentActionAPIEdit";
import AgentActionLLMEdit from "./AgentActionLLMEdit";
import AgentActionWebhookEdit from "./AgentActionWebhookEdit";
import AgentActionWorkflowEdit from "./AgentActionWorkflowEdit";
import {
  APIActionConfig,
  LLMActionConfig,
  WebhookActionConfig,
  WorkflowActionConfig,
  ActionConfig,
  ActionReference,
} from "../../types/AgentAction";

interface AgentActionEditProps {
  actionType?: "api_call" | "llm_call" | "webhook_creation" | "maitai_workflow";
  config: ActionConfig;
  onChange: (config: ActionConfig) => void;
  actionName?: string;
  description?: string;
  onActionNameChange?: (name: string) => void;
  onDescriptionChange?: (description: string) => void;
  onActionTypeChange?: (
    type: "api_call" | "llm_call" | "webhook_creation" | "maitai_workflow"
  ) => void;
  applicationId?: number;
  showSaveActions?: boolean;
  onSave?: () => void;
  onCancel?: () => void;
  isSaving?: boolean;
  isValid?: boolean;
  invocationMode?: "foreground" | "background";
  onInvocationModeChange?: (mode: "foreground" | "background") => void;
  whenToUse?: string;
  whenNotToUse?: string;
  onWhenToUseChange?: (text: string) => void;
  onWhenNotToUseChange?: (text: string) => void;
  references?: ActionReference[];
  onReferencesChange?: (references: ActionReference[]) => void;
}

const AgentActionEdit: React.FC<AgentActionEditProps> = ({
  actionType,
  config,
  onChange,
  actionName,
  description,
  onActionNameChange,
  onDescriptionChange,
  onActionTypeChange,
  applicationId,
  showSaveActions,
  onSave,
  onCancel,
  isSaving,
  isValid,
  invocationMode,
  onInvocationModeChange,
  whenToUse,
  whenNotToUse,
  onWhenToUseChange,
  onWhenNotToUseChange,
  references,
  onReferencesChange,
}) => {
  // Automatically set invocation mode to foreground when action type is webhook_creation
  useEffect(() => {
    if (actionType === "webhook_creation" && invocationMode !== "foreground") {
      onInvocationModeChange?.("foreground");
    }
  }, [actionType, invocationMode, onInvocationModeChange]);
  return (
    <div className="space-y-6">
      {/* Action Details */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="text-xl font-gc-med text-foreground">
            Action Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* First row: Action Name and Action Type */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Action Name */}
            <div className="space-y-3">
              <Label className="text-sm font-gc-med text-foreground">
                Action Name <span className="text-red-500">*</span>
              </Label>
              <Input
                value={actionName || ""}
                onChange={(e) => onActionNameChange?.(e.target.value)}
                placeholder="e.g., send_notification, get_user_data"
                className="font-mono"
              />
            </div>

            {/* Action Type */}
            <div className="space-y-3">
              <Label className="text-sm font-gc-med text-foreground">
                Action Type <span className="text-red-500">*</span>
              </Label>
              <Select
                value={actionType || ""}
                onValueChange={(value: "api_call" | "llm_call" | "webhook_creation" | "maitai_workflow") =>
                  onActionTypeChange?.(value)
                }>
                <SelectTrigger>
                  <SelectValue placeholder="Select action type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="api_call">
                    <div className="flex flex-col">
                      <span className="font-gc-med">API Call</span>
                      <span className="text-xs text-muted-foreground">
                        External service integration
                      </span>
                    </div>
                  </SelectItem>
                  <SelectItem value="llm_call">
                    <div className="flex flex-col">
                      <span className="font-gc-med">LLM Call</span>
                      <span className="text-xs text-muted-foreground">
                        AI reasoning and text generation
                      </span>
                    </div>
                  </SelectItem>
                  <SelectItem value="webhook_creation">
                    <div className="flex flex-col">
                      <span className="font-gc-med">Webhook Creation</span>
                      <span className="text-xs text-muted-foreground">
                        Create webhook for async callbacks
                      </span>
                    </div>
                  </SelectItem>
                  <SelectItem value="maitai_workflow">
                    <div className="flex flex-col">
                      <span className="font-gc-med">Maitai Workflow</span>
                      <span className="text-xs text-muted-foreground">
                        Run a multi-step workflow
                      </span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Second row: Invocation Mode and Response Type (API only) */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-gc-med text-foreground">
                  Invocation Mode
                </Label>
                <Tooltip content={actionType === "webhook_creation" ? "Webhook actions must be foreground to support orchestration and execution pausing" : "Determines how the action output is handled - foreground actions show results to users, background actions update context only"}>
                  <Info className="w-4 h-4 text-muted-foreground" />
                </Tooltip>
              </div>
              <Select
                value={actionType === "webhook_creation" ? "foreground" : (invocationMode || "foreground")}
                onValueChange={(value: "foreground" | "background") =>
                  onInvocationModeChange?.(value)
                }
                disabled={actionType === "webhook_creation"}>
                <SelectTrigger>
                  <SelectValue placeholder="Select invocation mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="foreground">
                    <div className="flex flex-col">
                      <span className="font-gc-med">Foreground</span>
                      <span className="text-xs text-muted-foreground">
                        User-facing output
                      </span>
                    </div>
                  </SelectItem>
                  {actionType !== "webhook_creation" && (
                    <SelectItem value="background">
                      <div className="flex flex-col">
                        <span className="font-gc-med">Background</span>
                        <span className="text-xs text-muted-foreground">
                          Updates context only
                        </span>
                      </div>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            {actionType === "api_call" && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-gc-med text-foreground">
                    Response Type
                  </Label>
                  <Tooltip content="Choose whether to make real API calls or return mock responses">
                    <Info className="w-4 h-4 text-muted-foreground" />
                  </Tooltip>
                </div>
                <Select
                  value={(config as APIActionConfig)?.response_type || "real"}
                  onValueChange={(value: "real" | "mock") => {
                    const current = config as APIActionConfig;
                    onChange({
                      ...current,
                      response_type: value,
                    } as APIActionConfig);
                  }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select response type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="real">
                      <div className="flex flex-col">
                        <span className="font-gc-med">Real</span>
                        <span className="text-xs text-muted-foreground">
                          Make actual API calls
                        </span>
                      </div>
                    </SelectItem>
                    <SelectItem value="mock">
                      <div className="flex flex-col">
                        <span className="font-gc-med">Mock</span>
                        <span className="text-xs text-muted-foreground">
                          Return predefined responses
                        </span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Usage Guidance */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="text-xl font-gc-med text-foreground">
            Usage Guidance
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* What It Does (moved from Description) */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-gc-med text-foreground">
                What It Does <span className="text-red-500">*</span>
              </Label>
              <Tooltip content="Define clearly what this action does, including side effects so the agent knows the extent of its capabilities.">
                <Info className="w-4 h-4 text-muted-foreground" />
              </Tooltip>
            </div>
            <Textarea
              value={description || ""}
              onChange={(e) => onDescriptionChange?.(e.target.value)}
              placeholder="e.g., This action sends a notification to the user."
              className="min-h-[100px]"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-gc-med text-foreground">
                  When To Use
                </Label>
                <Tooltip content="Situations where this action is appropriate. The planner uses this to select capabilities.">
                  <Info className="w-4 h-4 text-muted-foreground" />
                </Tooltip>
              </div>
              <Textarea
                value={whenToUse || ""}
                onChange={(e) => onWhenToUseChange?.(e.target.value)}
                placeholder="e.g., Use when the user asks to send a notification or confirm delivery status."
                className="min-h-[100px]"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-gc-med text-foreground">
                  When Not To Use
                </Label>
                <Tooltip content="Situations where this action should be avoided.">
                  <Info className="w-4 h-4 text-muted-foreground" />
                </Tooltip>
              </div>
              <Textarea
                value={whenNotToUse || ""}
                onChange={(e) => onWhenNotToUseChange?.(e.target.value)}
                placeholder="e.g., Do not use for billing issues or account deletion."
                className="min-h-[100px]"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Type-Specific Configuration */}
      {actionType === "api_call" ? (
        <AgentActionAPIEdit
          config={config as APIActionConfig}
          onChange={onChange}
          actionName={actionName}
          description={description}
          onActionNameChange={onActionNameChange}
          onDescriptionChange={onDescriptionChange}
          showSaveActions={showSaveActions}
          onSave={onSave}
          onCancel={onCancel}
          isSaving={isSaving}
          isValid={isValid}
        />
      ) : actionType === "llm_call" ? (
        <AgentActionLLMEdit
          config={config as LLMActionConfig}
          onChange={onChange}
          applicationId={applicationId}
          showSaveActions={showSaveActions}
          onSave={onSave}
          onCancel={onCancel}
          isSaving={isSaving}
          isValid={isValid}
          references={references}
          onReferencesChange={onReferencesChange}
        />
      ) : actionType === "webhook_creation" ? (
        <AgentActionWebhookEdit
          config={config as WebhookActionConfig}
          onChange={onChange}
          actionName={actionName}
          description={description}
          onActionNameChange={onActionNameChange}
          onDescriptionChange={onDescriptionChange}
          showSaveActions={showSaveActions}
          onSave={onSave}
          onCancel={onCancel}
          isSaving={isSaving}
          isValid={isValid}
        />
      ) : actionType === "maitai_workflow" ? (
        <AgentActionWorkflowEdit
          config={config as WorkflowActionConfig}
          onChange={onChange}
          actionName={actionName}
          description={description}
          onActionNameChange={onActionNameChange}
          onDescriptionChange={onDescriptionChange}
          showSaveActions={showSaveActions}
          onSave={onSave}
          onCancel={onCancel}
          isSaving={isSaving}
          isValid={isValid}
        />
      ) : actionType ? (
        <div className="text-center py-8 text-muted-foreground">
          <p className="text-sm">Unsupported action type: {actionType}</p>
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <p className="text-sm">Select an action type to configure</p>
        </div>
      )}
    </div>
  );
};

export default AgentActionEdit;
